<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="インターネットType4経路設定"
  >
    <template v-slot:body="{ cancel }" v-if="isLoaded">
      <validation-observer
        v-slot="{ handleSubmit }"
        :initial-values="{ type4RouteList: route.type4RouteList }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-4">
            <div class="modal-body">
              <v-table density="compact" class="border-less">
                <thead class="v-table-head">
                  <tr>
                    <th class="text-center th-address">
                      <app-badge
                        item="宛先"
                        :msg="
                          $filter.message(
                            'info_cidr_ip_address_of_network_address',
                            {
                              ipaddress1: '61.200.163.0/24',
                              genre: '宛先',
                              subnet1: '1',
                              subnet2: '30',
                              ipaddress2: '61.200.163.35/32',
                            }
                          )
                        "
                        placement="end"
                        :is-info="changeable"
                        :is-required="changeable && displayRequired"
                      ></app-badge>
                    </th>
                    <th class="th-memo">説明</th>
                    <th class="th-action"></th>
                  </tr>
                </thead>
                <tbody>
                  <validation-array
                    name="type4RouteList"
                    v-slot="{ fields, push, remove }"
                  >
                    <tr></tr>
                    <tr v-for="(item, index) in fields" :key="item.key">
                      <td>
                        <validation-provider
                          :name="`type4RouteList_${item.key}`"
                          v-model="route.type4RouteList[index].internetAddress"
                          label="宛先"
                          :rules="{
                            required: route.type4RouteList.length > 1,
                            is_ip_subnet: { min: 0, max: 32, exclude: [31] },
                            is_not_current_ip: true,
                            is_not_loopback_ip: true,
                            is_not_multicast_ip: true,
                            is_not_class_e_ip: true,
                            is_not_broadcast_ip: true,
                            is_network_address: true,
                            duplicate: { list: dstAddressList(index) },
                          }"
                          v-slot="{ componentField, errorMessage, meta }"
                        >
                          <app-form-input
                            v-bind="componentField"
                            :error-messages="errorMessage"
                            :field-meta="meta"
                            :placeholder="
                              !changeable
                                ? null
                                : $filter.message(
                                    'placeholder_global_ip_address_with_subnet32'
                                  )
                            "
                            :disabled="!changeable"
                          />
                        </validation-provider>
                      </td>
                      <td class="pt-2">
                        <template
                          v-if="route.type4RouteList[index].isMicrosoft365"
                        >
                          M365用自動設定
                        </template>
                      </td>
                      <td>
                        <v-btn
                          icon="$mdiMinus"
                          v-permission.config
                          size="small"
                          variant="outlined"
                          class="rounded-sm"
                          @click="removeRoute(index, remove)"
                        >
                        </v-btn>
                      </td>
                    </tr>
                    <tr>
                      <td></td>
                      <td></td>
                      <td>
                        <v-btn
                          icon="$mdiPlus"
                          v-permission.config
                          size="small"
                          variant="outlined"
                          class="rounded-sm"
                          @click="addRoute(push)"
                        >
                        </v-btn>
                      </td>
                    </tr>
                  </validation-array>
                </tbody>
              </v-table>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn
                  v-permission.config
                  type="submit"
                  variant="flat"
                  color="primary"
                  >設定
                </v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { Type4Service } from "@/apis/ContractChangeType4Api";
import type { PutType4Route, Type4Route } from "@/apis/Type4RouteApi";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "InternetType4RouteModify",
  props: {
    selectedItem: {
      type: Object as PropType<Type4Service>,
      required: true,
    },
  },
  data() {
    return {
      /** 初期通信完了フラグ */
      isLoaded: false,
      route: {} as Type4Route,
    };
  },
  computed: {
    /** true: 変更可能, false: 変更不可 */
    changeable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    /** 宛先が1件の場合は必須表示しない */
    displayRequired(): boolean {
      return this.route.type4RouteList.length > 1;
    },
    /**宛先の重複チェック用リスト作成 */
    dstAddressList() {
      return (index: number) => {
        return this.route.type4RouteList
          .filter((_, num) => num !== index)
          .map((e) => e.internetAddress);
      };
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.route = await this.$api.type4Route.getType4Route(
        this.selectedItem.type4Id
      );
      // 宛先アドレスの初期ソート対応
      this.route.type4RouteList.sort((a, b) => {
        return this.$service.sortCompare(a, b, [["internetAddress", "asc"]]);
      });

      // Type4経路情報リストが0件の場合、初期化
      if (this.route.type4RouteList.length === 0 && this.changeable) {
        this.route.type4RouteList = [
          {
            internetAddress: "",
            isMicrosoft365: false,
          },
        ];
      }
    },
    /** 経路設定を行う */
    async submit() {
      await this.$confirm("インターネット経路を変更します。よろしいですか？");
      const data: PutType4Route = {
        type4RouteKeyId: this.route.type4RouteKeyId,
        type4RouteList: this.route.type4RouteList,
      };
      if (
        data.type4RouteList.length === 1 &&
        !data.type4RouteList[0].internetAddress
      ) {
        data.type4RouteList = [];
      }
      await this.$api.type4Route.putType4Route(this.selectedItem.type4Id, data);
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** 経路設定の追加
     * @param push https://vee-validate.logaretm.com/v4/api/field-array/#slots
     * 上記場所のpushを参照
     */
    addRoute: function (push: (item: any) => void) {
      const addItem = {
        internetAddress: "",
        isMicrosoft365: false,
      };
      this.route.type4RouteList.push(addItem);
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },
    /**
     * 経路設定の削除
     * @param index 対象インデックス
     * @param push https://vee-validate.logaretm.com/v4/api/field-array/#slots
     * 上記場所のremoveを参照
     */
    removeRoute(index: number, remove: (idx: number) => void) {
      if (this.route.type4RouteList.length > 1) {
        this.route.type4RouteList.splice(index, 1);
        // ValidationにFieldを削除することを伝える
        remove(index);
      } else {
        this.route.type4RouteList[0].internetAddress = "";
        this.route.type4RouteList[0].isMicrosoft365 = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(.col-form-label) {
  padding-bottom: 0;
  .v-label,
  .badge-label {
    font-size: inherit;
    i {
      font-size: 20px;
    }
  }
}
.modal-body {
  min-height: 55vh;
}

table > tbody > tr:nth-of-type(1) {
  height: 4px;
}

th {
  text-align: center !important;
  height: 25px !important;
}
.th-address {
  width: 50%;
}
.th-memo {
  width: 20%;
}
.th-action {
  background-color: unset !important;
}
td {
  vertical-align: top;
}
</style>
